<template lang="html">
  <div
    ref="preview"
    class="preview"
  >
    <div
      ref="previewInner"
    >
      <section
        ref="playerContainer"
        :class="['player-container', { fixed: fixed }]"
      >
        <div
          ref="player"
          :style="{ height: `${ previewHeight }px`}"
          class="player"
        >
          <iframe
            allow="cross-origin-isolated"
            :style="iframeDimension"
            :src="templateUrl"
            crossorigin
          />
        </div>
        <div class="meta-container">
          <progress-bar
            :duration="duration"
            :progress="progress"
            color="#14c6ab"
          />
          <span class="timecode">{{ computedProgress.format('mm:ss') }}</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ProgressBar from '@/components/ProgressBar'
import bus from '@/bus'
import dayjs from 'dayjs'
import throttle from 'lodash.throttle'

export default {
  name: 'Preview',

  components: { ProgressBar },

  props: {
    templateUrl: { type: String, required: true }
  },

  data () {
    return {
      configWidth: 1920,
      configRatio: 16 / 9,
      previewWidth: 0,
      progress: 0,
      duration: 0,
      staticLimit: null,
      fixed: false
    }
  },

  computed: {
    previewHeight () {
      return this.previewWidth / this.configRatio
    },

    computedProgress () {
      return dayjs()
        .set('hour', 0)
        .set('minute', 0)
        .set('second', this.progress)
    },

    previewRatio () {
      return this.previewWidth / this.configWidth
    },

    iframeDimension () {
      return {
        zoom: this.previewRatio,
        transform: `scale(${this.previewRatio})`,
        transformOrigin: '0 0',
        width: `${1 / this.previewRatio * 100}%`,
        height: `${1 / this.previewRatio * 100}%`
      }
    },

    throttledUpdatePreviewWidth () {
      return throttle(this.updatePreviewWidth, 10)
    }
  },

  mounted () {
    this.addEventListeners()
    this.$nextTick(_ => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      const rect = this.$refs.preview.getBoundingClientRect()
      this.staticLimit = rect.top + scrollTop
      this.fixed = this.staticLimit - scrollTop <= 0
      window.addEventListener('scroll', this.stickyScroller)
      this.updatePreviewWidth()
    })
  },

  beforeDestroy () {
    this.removeEventListeners()
  },

  methods: {
    stickyScroller (e) {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      if (this.staticLimit - scrollTop <= 0) {
        if (!this.fixed) this.setFixed(true)
      } else {
        if (this.fixed) this.setFixed(false)
      }
    },

    setFixed (bool) {
      this.fixed = bool
      this.throttledUpdatePreviewWidth()
    },

    addEventListeners () {
      bus.$on('previsualize', this.sendConfig)

      window.onmessage = e => {
        if (e.data.type === 'animation-update') {
          this.progress = e.data.value
          bus.$emit('preview-progress', e.data.value / this.duration)
        }

        if (e.data.type === 'duration-update') {
          this.duration = e.data.value
        }

        if (e.data.type === 'default-filename') {
          bus.$emit('default-filename', e.data.value)
        }
      }

      window.addEventListener('resize', this.throttledUpdatePreviewWidth)
    },

    removeEventListeners () {
      bus.$off('previsualize', this.sendConfig)
      window.removeEventListener('resize', this.throttledUpdatePreviewWidth)
      window.removeEventListener('scroll', this.stickyScroller)
    },

    sendConfig (c) {
      this.configWidth = c.general.videoWidth
      this.configRatio = c.general.videoWidth / c.general.videoHeight
      console.log(c)
      window.frames.postMessage(
        { type: 'init-template-data', config: c }
        , '*'
      )
    },

    updatePreviewWidth () {
      this.$nextTick(() => {
        this.previewWidth = this.$refs.previewInner.clientWidth
        if (this.fixed) {
          this.$refs.player.style.width = this.$refs.previewInner.clientWidth + 'px'
        } else {
          this.$refs.player.style.width = 'auto'
        }
      })
    }
  }
}
</script>

<style lang="scss">
@import "~@/assets/styles/variables";

.preview {
  padding: 72px;
  transition: padding 0.3s ease;

  @media screen and (max-width: 1400px) {
    padding: 44px;
  }

  @media screen and (max-width: 1100px) {
    padding: 34px;
  }

  @media screen and (max-width: 900px) {
    padding: 12px;
  }

  .player-container{
    &.fixed {
      position: fixed;
      top: 72px;
      // margin-right: $main_padding_right;

      @media screen and (max-width: 1400px) {
        top: 44px;
      }

      @media screen and (max-width: 1100px) {
        top: 34px;
      }

      @media screen and (max-width: 900px) {
        top: 12px;
      }
    }
  }

  .player {
    position: relative;

    iframe {
      transform-origin: 0 0;
      display: block;
      margin: 0; padding: 0;
      border: none;
      overflow: hidden;
    }
  }

  .meta-container {
    position: relative;
    margin-top: 20px;
    text-align: right;
    font-family: $roboto_mono;

    .timecode {
      display: inline-block;
      margin-bottom: 0;
      margin-top: 10px;
      font-size: 15px;
    }
  }
}
</style>
